<template>
  <div :class="[{ 'p-error': errorMessage }]">
    <label v-if="$slots.label" class="c-image-upload-label subtitle-2">
      <slot name="label" />
    </label>
    <FileUpload
      ref="uploader"
      customUpload
      :accept="accept"
      :maxFileSize="maxFileSize"
      @select="onFileSelected"
      :disabled="disabled"
      :invalidFileSizeMessage="
        t('errorMessages.fileSize', { maxFileSize }, 'File cannot be bigger than {maxFileSize}')
      "
      :invalidFileTypeMessage="t('errorMessages.fileType', 'File format not supported')"
    />
    <div class="h-[16rem]">
      <div class="c-image-upload-file-container relative w-full mt-2 h-full">
        <div
          @dragover="onDragOver"
          @dragleave="onDragLeave"
          @drop="onDrop"
          @click="callFileUpload"
          :class="[
            'c-drop-zone-border w-full max-w-full h-full overflow-x-hidden absolute top-0 lef-0 border-1 border-dashed border-primary flex items-center justify-center flex-col',
            { 'c-image-upload-highlight': dragOver },
            { 'c-image-upload-drop-zone': !file },
            { 'cursor-pointer': !disabled },
            { 'cursor-not-allowed': disabled },
          ]"
        >
          <template v-if="!file">
            <span class="material-symbols-rounded text-[3rem] text-primary text-error"
              >file_upload</span
            >
            <h4 class="text-primary text-error max-w-full my-2">
              {{ t(`fileSelect.uploadFile`) }}
            </h4>
            <span class="info-text">
              {{
                t(`fileSelect.filesOnly`, { fileType: `${accept?.replace(', ', ' or ')}` })
              }}</span
            >
          </template>
          <template v-else>
            <span class="material-symbols-rounded text-[2rem] text-primary text-error"
              >file_present</span
            >
            <h4
              class="text-primary text-error max-w-full my-2 px-6 text-ellipsis overflow-hidden whitespace-nowrap"
            >
              {{ file?.name }}
            </h4>
            <span class="info-text">{{ `(${(file?.size / 1024).toFixed(1)}KB)` }} </span>
          </template>
        </div>
      </div>
    </div>

    <div id="file-select-error-message" class="p-error mt-2" v-if="errorMessage">
      <span class="caption">{{ errorMessage }}</span>
    </div>

    <span v-if="$slots.helperText" class="caption mt-2">
      <slot name="helperText" />
    </span>
    <div class="w-full flex">
      <Button
        v-if="file"
        :label="t(`fileSelect.remove`)"
        :class="`c-button-text mt-2 mx-auto ${ButtonStateClass.TERTIARY} ${ButtonSeverityClass.DANGER}`"
        @click="onRemoveClicked"
        icon="pi pi-trash"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FileUpload } from './../../../core/file/file-upload';
import { Button, ButtonSeverityClass, ButtonStateClass } from './../../../core/buttons';

const { t } = useI18n();

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  maxFileSize: {
    type: Number,
    default: null,
  },
  accept: {
    type: String,
    default: '.xlsx',
  },
});
const emit = defineEmits(['select', 'removeFile']);

const selectedFile = defineModel('file', {
  type: [File || null],
});

const uploader = ref(null);
const dragOver = ref(false);
const errorMessage = ref(null);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onDragEnter = (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
};

const onDragOver = (event: Event) => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();
    dragOver.value = true;
  }
};

const onDragLeave = () => {
  dragOver.value = false;
};

const onDrop = (event: Event) => {
  if (!props.disabled) {
    event.stopPropagation();
    event.preventDefault();

    dragOver.value = false;
    errorMessage.value = '';

    const files =
      (event as DragEvent).dataTransfer?.files || (event.target as HTMLInputElement).files;

    if (files?.length === 1) {
      uploader.value.onFileSelect(event);
    } else {
      errorMessage.value = t('errorsMessages.fileLimit', 'Only one file can be selected');
    }
  }
};

const onFileSelected = ({ files }: { files: any[] }) => {
  const file = files.length ? files[files.length - 1] : null;

  if (file) {
    selectedFile.value = file;
    errorMessage.value = '';

    if (files.length > 1) {
      uploader.value.remove(0);
    }

    emit('select', file);
  }
};

const removeFile = () => {
  if (selectedFile.value) {
    selectedFile.value = null;
    emit('removeFile');
  }
};

const onRemoveClicked = () => {
  errorMessage.value = null;
  uploader.value.clear();

  removeFile();
};

const callFileUpload = () => {
  uploader.value.choose();
};

watch(
  () => uploader.value?.messages,
  (newMessages, oldMessages) => {
    if (newMessages !== oldMessages && newMessages?.length) {
      errorMessage.value = newMessages[0];
    }
  },
);
</script>
<style scoped lang="scss">
.info-text {
  color: $neutralDarkL1Color;
}
.c-image-upload-file-container {
  background: $neutralLightD1Color;
}
:deep(.p-fileupload) {
  .p-fileupload-content,
  .p-fileupload-buttonbar button.p-button {
    display: none;
  }
  .p-fileupload-buttonbar {
    display: none;
  }
}
.c-image-upload {
  &-highlight {
    background-color: var(--neutral-light-color);
    opacity: 0.3;
  }
}
</style>
